import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Card,
  Divider,
  Checkbox,
  FormControlLabel,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GET, PUT } from "APIclient";
import properties from "constants/ApplicationConstants";
import LockIcon from "@mui/icons-material/Lock";

function EditGoalSettings() {
  const navigate = useNavigate();
  const { empId } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", type: "" });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [employeeName, setEmployeeName] = useState("");
  const [employeeEmpId, setEmployeeEmpId] = useState(null);
  const [managerId, setManagerId] = useState(null);
  const [appraisalId, setAppraisalId] = useState(null);

  const [managerPermission, setManagerPermission] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [formEditDeadline, setFormEditDeadline] = useState(null);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);

  const [technicalPrimary, setTechnicalPrimary] = useState([]);
  const [allTechnicalPrimary, setAllTechnicalPrimary] = useState([]);
  const [selectedTechnicalPrimary, setSelectedTechnicalPrimary] = useState([]);

  useEffect(() => {
    const storedEmpId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
    if (storedEmpId) {
      const parsedManagerId = parseInt(storedEmpId);
      setManagerId(parsedManagerId);
      initializeData(parsedManagerId);
    } else {
      showNotification("Manager ID not found", "error");
      setLoading(false);
    }
  }, [empId]);

  const fetchAllTechnicalPrimarySkills = async (managerId, selectedSkills = [], technicalPrimaryFromGoals = []) => {
    try {
      const response = await GET(`manager/list-kraSkills/${managerId}`);
      if (response.data?.data) {
        const technicalPrimaryFromKra = response.data.data.filter(skill => {
          const isTechnicalPrimary = skill.skillTypes.toLowerCase().replace(/[-\s]/g, '') === 'technicalprimary';
          return isTechnicalPrimary && skill.status === 1;
        });
        const selectedSkillsFromGoals = technicalPrimaryFromGoals.filter(s => s.selected);
        const goalsSkillIds = technicalPrimaryFromGoals.map(s => s.paFormId);
        const unselectedFromKra = technicalPrimaryFromKra.filter(s => !goalsSkillIds.includes(s.paFormId));
        setAllTechnicalPrimary([...selectedSkillsFromGoals, ...unselectedFromKra]);
      }
    } catch (error) {
      console.error("Error fetching all technical primary skills:", error);
      const selectedSkillsFromGoals = technicalPrimaryFromGoals.filter(s => s.selected);
      if (selectedSkillsFromGoals.length > 0) {
        setAllTechnicalPrimary(selectedSkillsFromGoals);
      } else {
        showNotification("Failed to load available skills", "error");
      }
    }
  };

  const fetchEmployeeGoals = async (appraisalId, employeeId, managerId) => {
    try {
      const response = await GET(`manager/${appraisalId}/employee/${employeeId}/goals`);
      if (response.data?.data) {
        const goalsData = response.data.data;
        setEmployeeName(goalsData.empName || "");
        setEmployeeEmpId(goalsData.empId || employeeId);
        const processSkills = skills => skills.map(skill => ({ paFormId: skill.paFormId, skillSet: skill.skillSet, selected: skill.selected || false }));
        const techPrimary = processSkills(goalsData.technicalPrimary || []);
        setTechnicalPrimary(techPrimary);
        const initialSelected = techPrimary.filter(s => s.selected).map(s => s.paFormId);
        setSelectedTechnicalPrimary(initialSelected);
        await fetchAllTechnicalPrimarySkills(managerId, initialSelected, techPrimary);
      }
    } catch (error) {
      console.error("Error fetching employee goals:", error);
      showNotification(error.response?.data?.message || "Failed to load employee goals", "error");
    }
  };

  const checkManagerPermission = async (managerId, employeeId, deadlinePassed = false) => {
    try {
      const response = await GET(`manager/reportees/${managerId}`);
      if (response.data?.data) {
        const reportees = Array.isArray(response.data.data) ? response.data.data : [];
        const employee = reportees.find(emp => emp.empId === parseInt(employeeId));
        if (employee) {
          setEmployeeEmpId(employee.empId);
          const permission = employee.managerPermission;
          const empPermission = employee.empPermission;
          setManagerPermission(permission);
          // Only editable if deadline not passed AND both permissions are granted
          setIsEditable(permission === 1 && empPermission === 1 && !deadlinePassed);
          if (deadlinePassed) {
            // Deadline message is shown separately; skip permission warning here
          } else if (permission === 0 || empPermission === 0) {
            showNotification("You don't have permission to edit goals for this employee at this time", "warning");
          }
          return permission;
        } else {
          showNotification("Employee not found in your reportees list", "error");
          setIsEditable(false);
          return 0;
        }
      }
    } catch (error) {
      console.error("Error checking manager permission:", error);
      showNotification("Failed to verify permissions", "error");
      setIsEditable(false);
      return 0;
    }
  };

  const initializeData = async (managerId) => {
    try {
      setLoading(true);

      // Fetch current appraisal and validate deadline first
      const permissionResponse = await GET(`manager/appraisal/current`);
      if (permissionResponse.data?.data) {
        const appraisalData = permissionResponse.data.data;
        const currentAppraisalId = appraisalData.appraisalId;
        setAppraisalId(currentAppraisalId);

        // Deadline validation
        const deadline = appraisalData.formEditDeadline;
        setFormEditDeadline(deadline);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deadlineDate = new Date(deadline);
        deadlineDate.setHours(0, 0, 0, 0);
        const deadlinePassed = today > deadlineDate;
        setIsDeadlinePassed(deadlinePassed);

        // Check permissions passing deadlinePassed directly to avoid stale state
        await checkManagerPermission(managerId, empId, deadlinePassed);

        if (deadlinePassed) {
          showNotification(
            `The goal edit deadline was ${deadline}. Editing is no longer allowed.`,
            "warning"
          );
        }

        await fetchEmployeeGoals(currentAppraisalId, empId, managerId);
      } else {
        showNotification("No active appraisal found", "error");
      }
    } catch (error) {
      console.error("Error initializing data:", error);
      showNotification("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTechnicalPrimaryToggle = (paFormId) => {
    if (!isEditable) { showNotification("You don't have permission to edit goals", "warning"); return; }
    setSelectedTechnicalPrimary(prev => prev.includes(paFormId) ? prev.filter(id => id !== paFormId) : [...prev, paFormId]);
  };

  const handleSelectAllTechnicalPrimary = () => {
    if (!isEditable) { showNotification("You don't have permission to edit goals", "warning"); return; }
    const allIds = allTechnicalPrimary.map(s => s.paFormId);
    const allSelected = allIds.every(id => selectedTechnicalPrimary.includes(id));
    setSelectedTechnicalPrimary(allSelected ? [] : allIds);
  };

  const handleSubmit = () => {
    if (!isEditable) { showNotification("You don't have permission to edit goals", "warning"); return; }
    if (selectedTechnicalPrimary.length === 0) { showNotification("Please select at least one technical primary skill", "warning"); return; }
    setOpenConfirmDialog(true);
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      setOpenConfirmDialog(false);
      const response = await PUT(`manager/employee/${empId}/goals?managerId=${managerId}`, { appraisalId, selectedSkills: selectedTechnicalPrimary });
      if (response.status === 200) {
        showNotification("Goals updated successfully! Employee will be notified.", "success");
        setTimeout(() => navigate("/manager/appraisal/kraList"), 2000);
      }
    } catch (error) {
      console.error("Error updating goals:", error);
      showNotification(error.response?.data?.message || "Failed to update goals", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ open: true, message, type });
    setTimeout(() => setNotification({ open: false, message: "", type: "" }), 4000);
  };

  // Reusable employee info bar
  const EmployeeInfoBar = ({ mb = 0.75 }) => (
    <MDBox
      display="flex" alignItems="center" gap={1.5} mb={mb}
      px={1} py={0.5}
      sx={{ backgroundColor: "#f8f9fa", borderRadius: 1, border: "1px solid #e9ecef" }}
    >
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDTypography variant="caption" fontWeight="medium" color="text" sx={{ fontSize: "0.78rem" }}>
          Employee Name:
        </MDTypography>
        <MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.78rem" }}>
          {employeeName || "—"}
        </MDTypography>
      </MDBox>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDTypography variant="caption" fontWeight="medium" color="text" sx={{ fontSize: "0.78rem" }}>
          Employee ID:
        </MDTypography>
        <MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.78rem" }}>
          {employeeEmpId || empId || "—"}
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  const renderEditableTechnicalPrimarySection = () => {
    if (allTechnicalPrimary.length === 0) return null;
    const allSelected = allTechnicalPrimary.every(s => selectedTechnicalPrimary.includes(s.paFormId));

    return (
      <MDBox mb={0.75}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.4}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <MDTypography variant="subtitle2" fontWeight="medium">
              Technical Primary Skills
            </MDTypography>
            <MDTypography variant="caption" color={isEditable ? "info" : "error"} sx={{ fontSize: "0.72rem" }}>
              {isEditable ? "(Editable)" : "(View Only)"}
            </MDTypography>
            {!isEditable && <LockIcon sx={{ fontSize: 14, color: "error.main" }} />}
          </Box>
          <MDButton
            variant="text" color="info" size="small"
            onClick={handleSelectAllTechnicalPrimary}
            disabled={!isEditable}
            sx={{ py: 0, minHeight: 0, fontSize: "0.75rem" }}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </MDButton>
        </MDBox>

        <Box sx={{
          backgroundColor: isEditable ? "#fafafa" : "#f5f5f5",
          borderRadius: 1, p: 0.75,
          border: `1px solid ${isEditable ? "#ddd" : "#bdbdbd"}`,
          opacity: isEditable ? 1 : 0.7,
        }}>
          <Grid container spacing={0.25}>
            {allTechnicalPrimary.map(skill => (
              <Grid item xs={12} sm={6} key={skill.paFormId}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTechnicalPrimary.includes(skill.paFormId)}
                      onChange={() => handleTechnicalPrimaryToggle(skill.paFormId)}
                      color="primary"
                      disabled={!isEditable}
                      size="small"
                      sx={{ py: 0.25, pl: 1 }}
                    />
                  }
                  label={<MDTypography variant="body2" sx={{ fontSize: "0.82rem" }}>{skill.skillSet}</MDTypography>}
                  disabled={!isEditable}
                  sx={{ m: 0, display: "flex", alignItems: "center" }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Alert severity={isEditable ? "info" : "warning"} sx={{ mt: 0.5, py: 0.15, "& .MuiAlert-message": { py: 0.15 } }}>
          <MDTypography variant="caption" sx={{ fontSize: "0.78rem" }}>
            {isEditable
              ? `${selectedTechnicalPrimary.length} of ${allTechnicalPrimary.length} technical primary skills selected`
              : isDeadlinePassed
                ? `Editing is disabled because the goal edit deadline (${formEditDeadline}) has passed.`
                : "You cannot edit goals at this time."}
          </MDTypography>
        </Alert>
      </MDBox>
    );
  };

  // Selected skills list used in confirmation dialog
  const selectedSkillsList = allTechnicalPrimary.filter(s => selectedTechnicalPrimary.includes(s.paFormId));

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1.5}>
        <Card>
          {/* Header */}
          <MDBox
            variant="gradient"
            sx={{ background: properties.colors.bgcolorbluethem }}
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5} mt={0.75} p={0.75}
            textAlign="center"
          >
            <MDTypography variant="h5" fontWeight="medium" color="white" sx={{ fontSize: "1rem" }}>
              {isEditable ? "Edit Goal Settings" : "View Goal Settings"}
            </MDTypography>
            <MDTypography variant="body2" color="white" opacity={0.85} sx={{ fontSize: "0.75rem" }}>
              {isEditable
                ? "Modify technical primary skills assigned to this employee"
                : isDeadlinePassed
                  ? `Goal edit deadline has passed (${formEditDeadline}). This form is now read-only.`
                  : "View-only mode — You don't have permission to edit at this time"}
            </MDTypography>
          </MDBox>

          <MDBox p={0.75}>
            {/* Employee Info */}
            <EmployeeInfoBar mb={0.75} />

            {/* Deadline Banner */}
            {formEditDeadline && (
              <Alert
                severity={isDeadlinePassed ? "error" : "info"}
                sx={{ mb: 0.75, py: 0.15, "& .MuiAlert-message": { py: 0.15 } }}
              >
                <MDTypography variant="caption" sx={{ fontSize: "0.78rem" }}>
                  {isDeadlinePassed
                    ? `Goal edit deadline has passed (${formEditDeadline}). Editing is no longer allowed.`
                    : `Goal editing is open until ${formEditDeadline}.`}
                </MDTypography>
              </Alert>
            )}

            {/* Skills Section */}
            {renderEditableTechnicalPrimarySection()}

            {/* Action Buttons */}
            <MDBox display="flex" justifyContent="flex-end" gap={0.75} mt={0.5}>
              <MDButton
                variant="outlined" color="dark" size="small"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                {isEditable ? "Cancel" : "Back"}
              </MDButton>
              {isEditable && (
                <MDButton
                  variant="gradient" color="info" size="small"
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={{ background: properties.colors.bgcolorbluethem }}
                >
                  {submitting ? "Saving..." : "Update Goals"}
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
          maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ pb: 0.5, pt: 1.25, px: 1.5 }}>
            <MDTypography variant="subtitle1" fontWeight="medium" sx={{ fontSize: "0.95rem" }}>
              Confirm Technical Primary Skills Update
            </MDTypography>
          </DialogTitle>

          <DialogContent dividers sx={{ px: 1.5, py: 0.75 }}>
            <EmployeeInfoBar mb={0.75} />

            <Alert severity="warning" sx={{ mb: 0.75, py: 0.15, "& .MuiAlert-message": { py: 0.15 } }}>
              <MDTypography variant="caption" sx={{ fontSize: "0.79rem" }}>
                The employee will be notified about the updated technical primary skills via email.
              </MDTypography>
            </Alert>

            <MDTypography variant="subtitle2" fontWeight="medium" mb={0.4} sx={{ fontSize: "0.82rem" }}>
              Selected Technical Primary Skills ({selectedSkillsList.length})
            </MDTypography>

            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
              {selectedSkillsList.map((skill, index) => (
                <Box
                  key={skill.paFormId}
                  sx={{
                    px: 1, py: 0.4,
                    display: "flex", alignItems: "center", gap: 0.75,
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff",
                    borderBottom: index === selectedSkillsList.length - 1 ? "none" : "1px solid #eee",
                  }}
                >
                  <Typography sx={{ fontSize: "0.78rem", color: "#888", minWidth: 20 }}>
                    {index + 1}.
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem" }}>
                    {skill.skillSet}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 1.5, py: 0.75, gap: 0.5 }}>
            <MDButton
              variant="outlined" color="dark" size="small"
              onClick={() => setOpenConfirmDialog(false)}
              disabled={submitting}
            >
              Back
            </MDButton>
            <MDButton
              variant="gradient" color="info" size="small"
              onClick={handleConfirm}
              disabled={submitting}
              sx={{ background: properties.colors.bgcolorbluethem }}
            >
              {submitting ? (
                <><CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} />Confirming...</>
              ) : "Confirm"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Notification */}
        {notification.open && (
          <MDBox sx={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: 280 }}>
            <Alert
              severity={notification.type}
              onClose={() => setNotification({ open: false, message: "", type: "" })}
              sx={{ fontSize: "0.82rem" }}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default EditGoalSettings;